/** @typedef {'desktop' | 'mobile' | 'lowPower'} FloorPlanRenderTier */

const DEV_AO_STORAGE_KEY = 'myroomsai.floorPlanAoEnabled';

/**
 * Top-level shared floor-plan 3D render switches.
 */
export const FLOOR_PLAN_RENDER_CONFIG = {
  ambientOcclusionEnabled: true,
};

/** Shared floor-plan 3D render quality defaults (metre-based scenes). */
export const FLOOR_PLAN_RENDER_QUALITY = {
  ambientOcclusion: {
    radius: 0.18,
    intensity: 0.32,
    distanceFalloff: 2.1,
    halfRes: true,
    qualityMode: 'High',
  },
  /** Bright neutral fill — vertical white walls need even hemisphere/ambient, moderate key. */
  lighting: {
    hemisphereSky: '#ffffff',
    hemisphereGround: '#ffffff',
    hemisphereIntensity: 1.0,
    ambientIntensity: 1.15,
    keyDirectionalIntensity: 0.55,
    fillDirectionalIntensity: 0.3,
    upFillDirectionalIntensity: 0.1,
  },
  pixelRatioMax: 2,
};

const MOBILE_AO_PRESET = {
  radius: 0.16,
  intensity: 0.28,
  distanceFalloff: 2.0,
  halfRes: true,
  qualityMode: 'Medium',
};

const LOW_POWER_AO_PRESET = {
  radius: 0.12,
  intensity: 0.2,
  distanceFalloff: 2.0,
  halfRes: true,
  qualityMode: 'Performance',
};

/**
 * Whether ambient occlusion compositing is active for the current session.
 * @returns {boolean}
 */
export function isFloorPlanAmbientOcclusionEnabled() {
  return FLOOR_PLAN_RENDER_CONFIG.ambientOcclusionEnabled;
}

/**
 * Development-only AO preview toggle (defaults to on).
 * @returns {boolean}
 */
export function readDevAmbientOcclusionPreviewEnabled() {
  if (process.env.NODE_ENV !== 'development') return true;
  if (typeof window === 'undefined') return true;
  const stored = window.localStorage.getItem(DEV_AO_STORAGE_KEY);
  if (stored === null) return true;
  return stored === '1';
}

/**
 * @param {boolean} enabled
 */
export function writeDevAmbientOcclusionPreviewEnabled(enabled) {
  if (process.env.NODE_ENV !== 'development') return;
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(DEV_AO_STORAGE_KEY, enabled ? '1' : '0');
}

/**
 * Dev wall material test mode from URL: ?floorPlanWallTest=basic|standard
 * @returns {'basic' | 'standard' | null}
 */
export function readDevWallMaterialTestMode() {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') return null;
  const mode = new URLSearchParams(window.location.search).get('floorPlanWallTest');
  if (mode === 'basic' || mode === 'standard') return mode;
  return null;
}

/**
 * @returns {FloorPlanRenderTier}
 */
export function detectFloorPlanRenderTier() {
  if (typeof window === 'undefined') return 'desktop';

  const coarseMobile = window.matchMedia?.('(max-width: 768px), (pointer: coarse)').matches;
  const lowMemory = typeof navigator !== 'undefined' && Number(navigator.deviceMemory) > 0
    && navigator.deviceMemory <= 4;
  const lowConcurrency = typeof navigator !== 'undefined' && Number(navigator.hardwareConcurrency) > 0
    && navigator.hardwareConcurrency <= 4;

  if (coarseMobile && (lowMemory || lowConcurrency)) {
    return 'lowPower';
  }
  if (coarseMobile) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * @param {{
 *   tier?: FloorPlanRenderTier;
 *   ambientOcclusion?: Partial<typeof FLOOR_PLAN_RENDER_QUALITY.ambientOcclusion>;
 *   lighting?: Partial<typeof FLOOR_PLAN_RENDER_QUALITY.lighting>;
 *   pixelRatioMax?: number;
 * }} [overrides]
 */
export function resolveFloorPlanRenderQuality(overrides = {}) {
  const tier = overrides.tier ?? detectFloorPlanRenderTier();
  const base = FLOOR_PLAN_RENDER_QUALITY;
  const aoEnabled = isFloorPlanAmbientOcclusionEnabled();

  /** @type {typeof base.ambientOcclusion & { enabled: boolean }} */
  let ambientOcclusion = {
    ...base.ambientOcclusion,
    enabled: aoEnabled,
    ...overrides.ambientOcclusion,
  };

  if (tier === 'mobile' && aoEnabled) {
    ambientOcclusion = { ...ambientOcclusion, ...MOBILE_AO_PRESET, ...overrides.ambientOcclusion, enabled: aoEnabled };
  } else if (tier === 'lowPower') {
    ambientOcclusion = { ...ambientOcclusion, ...LOW_POWER_AO_PRESET, enabled: false, ...overrides.ambientOcclusion };
  }

  return {
    tier,
    ambientOcclusion,
    lighting: { ...base.lighting, ...overrides.lighting },
    pixelRatioMax: overrides.pixelRatioMax ?? base.pixelRatioMax,
  };
}
